// Global SVG defs (clip-art filter + pine-tree silhouette) — rendered once and referenced via url() / xlink
export default function GlobalSvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
      <defs>
        {/* Clip-art / illustrated posterize filter — gentle */}
        <filter id="clip-art" x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" result="blur" />
          <feColorMatrix in="blur" type="saturate" values="1.18" result="sat" />
          <feComponentTransfer in="sat">
            <feFuncR type="discrete" tableValues="0.04 0.16 0.28 0.4 0.52 0.64 0.78 0.92 1" />
            <feFuncG type="discrete" tableValues="0.04 0.16 0.28 0.4 0.52 0.64 0.78 0.92 1" />
            <feFuncB type="discrete" tableValues="0.04 0.16 0.28 0.4 0.52 0.64 0.78 0.92 1" />
          </feComponentTransfer>
        </filter>

        {/* Single pine tree shape — for use across the site */}
        <symbol id="pine" viewBox="0 0 40 80">
          <path d="M20 2 L8 22 L14 22 L4 40 L12 40 L0 60 L17 60 L17 78 L23 78 L23 60 L40 60 L28 40 L36 40 L32 22 L32 22 L20 2 Z" fill="currentColor" />
        </symbol>

        {/* Palm tree silhouette */}
        <symbol id="palm" viewBox="0 0 60 100">
          {/* trunk */}
          <path d="M30 35 Q27 60 28 95 L32 95 Q33 60 30 35 Z" fill="currentColor" />
          {/* fronds */}
          <path d="M30 32 C 22 18, 8 16, 2 18 C 8 22, 16 26, 28 34 Z" fill="currentColor" />
          <path d="M30 32 C 38 18, 52 16, 58 18 C 52 22, 44 26, 32 34 Z" fill="currentColor" />
          <path d="M30 30 C 30 12, 22 0, 14 0 C 18 8, 22 18, 30 34 Z" fill="currentColor" />
          <path d="M30 30 C 30 12, 38 0, 46 0 C 42 8, 38 18, 30 34 Z" fill="currentColor" />
          <path d="M30 30 C 36 22, 50 22, 56 30 C 48 30, 38 32, 30 36 Z" fill="currentColor" />
          <path d="M30 30 C 24 22, 10 22, 4 30 C 12 30, 22 32, 30 36 Z" fill="currentColor" />
          {/* coconuts */}
          <circle cx="28" cy="36" r="2" fill="currentColor" />
          <circle cx="32" cy="36" r="2" fill="currentColor" />
        </symbol>

        {/* Sunburst */}
        <symbol id="sun" viewBox="0 0 200 200">
          <circle cx="100" cy="100" r="36" fill="currentColor" />
          {Array.from({ length: 16 }).map((_, i) => {
            const a = (i * 360) / 16;
            return (
              <rect key={i} x="98" y="6" width="4" height="34" fill="currentColor" transform={`rotate(${a} 100 100)`} />
            );
          })}
        </symbol>

        {/* Saguaro cactus */}
        <symbol id="cactus" viewBox="0 0 40 70">
          <path d="M16 12 Q12 12 12 18 L12 30 Q12 34 8 34 L4 34 Q0 34 0 38 L0 50 Q0 56 6 56 L6 38 Q6 38 8 38 L12 38 Q18 38 18 32 L18 64 Q18 68 20 68 L20 68 Q22 68 22 64 L22 28 Q22 24 26 24 L30 24 Q34 24 34 28 L34 42 Q34 48 28 48 L28 28 Q28 28 26 28 L26 26 L22 26 L22 18 Q22 12 16 12 Z" fill="currentColor" />
        </symbol>
      </defs>
    </svg>
  );
}

// Decorative California silhouette strip — palms + pines mixed
export function PineBand({ tone = "#1f2e1f", className = "" }) {
  const pattern = [
    { type: "pine", scale: 0.95 },
    { type: "palm", scale: 1.1 },
    { type: "pine", scale: 0.75 },
    { type: "pine", scale: 0.9 },
    { type: "palm", scale: 1.25 },
    { type: "pine", scale: 0.85 },
    { type: "pine", scale: 1.05 },
    { type: "palm", scale: 1.0 },
    { type: "pine", scale: 0.7 },
    { type: "pine", scale: 0.95 },
    { type: "palm", scale: 1.15 },
    { type: "pine", scale: 0.8 },
    { type: "pine", scale: 1.0 },
    { type: "palm", scale: 1.05 },
    { type: "pine", scale: 0.85 },
    { type: "pine", scale: 0.95 },
    { type: "palm", scale: 1.2 },
    { type: "pine", scale: 0.75 },
  ];
  return (
    <div className={`flex items-end justify-between w-full overflow-hidden ${className}`} style={{ color: tone }} aria-hidden="true">
      {pattern.map((it, i) => (
        it.type === "palm" ? (
          <svg key={i} width="36" height={`${66 * it.scale}`} viewBox="0 0 60 100" className="shrink-0">
            <use href="#palm" />
          </svg>
        ) : (
          <svg key={i} width="30" height={`${44 * it.scale}`} viewBox="0 0 40 80" className="shrink-0">
            <use href="#pine" />
          </svg>
        )
      ))}
    </div>
  );
}

// Sun motif — fixed-position decorative element
export function SunMotif({ tone = "#f5b85c", size = 200, className = "" }) {
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ color: tone }} className={className} aria-hidden="true">
      <use href="#sun" />
    </svg>
  );
}
